import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import IconButton from '@mui/joy/IconButton';
import Dropdown from '@mui/joy/Dropdown';
import Menu from '@mui/joy/Menu';
import MenuButton from '@mui/joy/MenuButton';
import MenuItem from '@mui/joy/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import ConfirmDialog from 'components/core/ConfirmDialog';
import lookupTableActions from 'actions/lookupTableActions';
import taxonomyActions from 'actions/taxonomyActions';
import evidenceActions from 'actions/evidenceActions';
import observationActions from 'actions/observationActions';
import eventActions from 'actions/eventActions';
import locationActions from 'actions/locationActions';

const DossierActionButtons = ({ className, id }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const handleEdit = () => {
        switch (className) {
            case 'Event':
                navigate(`/event/${id}`);
                break;
            case 'Location':
                navigate(`/location/${id}/edit`);
                break;
            case 'Taxonomy':
                navigate(`/taxonomy/${id}/edit`);
                break;
            case 'Observation':
                navigate(`/observation/${id}/edit`);
                break;
            case 'Evidence':
                navigate(`/evidence/${id}/edit`);
                break;
            default:
                console.warn(`No edit route defined for ${className}`);
        }
    };

    const handleDeleteClick = () => {
        setShowDeleteDialog(true);
    };

    const handleDeleteCancel = () => {
        setShowDeleteDialog(false);
    };

    const handleDeleteConfirm = async () => {
        setShowDeleteDialog(false);
        try {
            let result;
            switch (className) {
                case 'Taxonomy':
                    result = await dispatch(taxonomyActions.deleteTaxonomy(id));
                    if (result.value) {
                        console.log(`Deleted taxonomy with ${result.value.deletedNamesCount} names and ${result.value.deletedIdentifiersCount} identifiers`);
                    }
                    break;
                case 'Evidence':
                    result = await dispatch(evidenceActions.deleteEvidence(id));
                    if (result.value) {
                        console.log(`Deleted evidence with ${result.value.deletedSubjectsCount} subjects, ${result.value.deletedVouchersCount} vouchers, ${result.value.deletedLiteraturesCount} literatures, ${result.value.deletedFilesCount} files, and ${result.value.deletedObservationEvidencesCount} observation links`);
                    }
                    break;
                case 'Observation':
                    result = await dispatch(observationActions.deleteObservation(id));
                    if (result.value) {
                        console.log(`Deleted observation with ${result.value.deletedObservationEvidencesCount} evidence links and ${result.value.deletedNotesCount} notes`);
                    }
                    break;
                case 'Event':
                    result = await dispatch(eventActions.remove(id));
                    if (result.value) {
                        console.log(`Deleted event with ${result.value.deletedNotesCount} notes`);
                    }
                    break;
                case 'Location':
                    result = await dispatch(locationActions.remove(id));
                    if (result.value) {
                        console.log(`Deleted location with ${result.value.deletedNotesCount} notes`);
                    }
                    break;
                default:
                    await dispatch(lookupTableActions.deleteRecord(className, id));
            }
            navigate('/');
        } catch (error) {
            console.error('Delete failed:', error);
            alert(`Failed to delete ${className.toLowerCase()}: ${error.message}`);
        }
    };

    return (
        <>
            <Dropdown>
                <MenuButton
                    slots={{ root: IconButton }}
                    slotProps={{ root: { variant: 'outlined', color: 'neutral', size: 'sm' } }}
                >
                    <MoreVertIcon />
                </MenuButton>
                <Menu>
                    <MenuItem onClick={handleEdit}>
                        <EditIcon sx={{ mr: 1 }} />
                        Edit
                    </MenuItem>
                    <MenuItem onClick={handleDeleteClick} color="danger">
                        <DeleteIcon sx={{ mr: 1 }} />
                        Delete
                    </MenuItem>
                </Menu>
            </Dropdown>
            
            <ConfirmDialog
            title={`Delete ${className}`}
            visible={showDeleteDialog}
            destructive={true}
            cancelHandler={handleDeleteCancel}
            cancelText="Cancel"
            okHandler={handleDeleteConfirm}
            okText="Delete"
        >
            Are you sure you want to delete this {className.toLowerCase()}?
            {className === 'Taxonomy' && (
                <><br /><br />This will also delete all associated taxonomy names and alternate identifiers.</>
            )}
            {className === 'Evidence' && (
                <><br /><br />This will also delete all associated subjects, vouchers, literature records, files, and observation links.</>
            )}
            {className === 'Observation' && (
                <><br /><br />This will also delete all associated evidence links and notes.</>
            )}
            {className === 'Event' && (
                <><br /><br />This will also delete all associated notes. Note: You cannot delete an event that has observations or evidence referencing it.</>
            )}
            {className === 'Location' && (
                <><br /><br />This will also delete all associated notes. Note: You cannot delete a location that has child locations or observations referencing it.</>
            )}
        </ConfirmDialog>
        </>
    );
};

DossierActionButtons.propTypes = {
    className: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired
};

export default DossierActionButtons;